/**
 * CRM Tab — Contacts + Leads (Sprint 2026-02-25)
 *
 * Two sub-tabs:
 *   • CONTACTS — base entity table with dedupe on phone/email
 *   • LEADS    — created from a contact; status chips, filters, comments
 *
 * All API calls go to /api/admin/crm/*. This module is self-contained and does
 * not read/write any other module's collections directly.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import api from '../../services/api';
import { getApiError } from '../../utils/errorHelper';
import {
  Search, Plus, X, Save, Loader2, Users, Phone, Mail, Building2,
  MessageSquare, Calendar, ChevronRight, RefreshCw, ChevronLeft,
  Trophy, XCircle, Clock, CheckCircle2, Filter, Contact as ContactIcon,
  ArrowUp, ArrowDown,
} from 'lucide-react';

const INR = (n) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 })
    .format(Number(n || 0));

const STATUS_META = {
  new:          { label: 'New',          cls: 'bg-blue-500/15 text-blue-300 border-blue-500/30' },
  contacted:    { label: 'Contacted',    cls: 'bg-amber-500/15 text-amber-300 border-amber-500/30' },
  closed_won:   { label: 'Closed-Won',   cls: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' },
  closed_lost:  { label: 'Closed-Lost',  cls: 'bg-slate-500/15 text-slate-300 border-slate-500/30' },
};

const MANUAL_SOURCES = [
  { value: 'whatsapp',    label: 'WhatsApp' },
  { value: 'phone_call',  label: 'Phone Call' },
  { value: 'referral',    label: 'Referral' },
  { value: 'walk_in',     label: 'Walk-in' },
  { value: 'linkedin',    label: 'LinkedIn' },
];
const AUTO_SOURCE_LABEL = {
  website: 'Website',
  'mp-startup-benefits': 'MP Startup',
  'dsc-renewal': 'DSC Renewal',
  'din-renewal': 'DIN Renewal',
};
const sourceLabel = (v) =>
  AUTO_SOURCE_LABEL[v] || MANUAL_SOURCES.find((m) => m.value === v)?.label || v;

const CONTACT_TYPE_META = {
  manual:   { label: 'Manual',   cls: 'bg-blue-500/15 text-blue-300' },
  director: { label: 'Director', cls: 'bg-amber-500/15 text-amber-300' },
  client:   { label: 'Client',   cls: 'bg-emerald-500/15 text-emerald-300' },
};

const todayISO = () => new Date().toISOString().slice(0, 10);
const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }); }
  catch { return iso; }
};
const fmtTime = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('en-IN', {
      day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true,
    });
  } catch { return ''; }
};

// ─── crmApi ──────────────────────────────────────────────────────────────────
const crmApi = {
  listContacts: (p) => api.get('/admin/crm/contacts', { params: p }).then(r => r.data),
  createContact: (p) => api.post('/admin/crm/contacts', p).then(r => r.data),
  updateContact: (id, p) => api.patch(`/admin/crm/contacts/${id}`, p).then(r => r.data),
  backfillContacts: () => api.post('/admin/crm/contacts/backfill').then(r => r.data),
  listLeads: (p) => api.get('/admin/crm/leads', { params: p }).then(r => r.data),
  leadsSummary: (p) => api.get('/admin/crm/leads/summary', { params: p }).then(r => r.data),
  createLead: (p) => api.post('/admin/crm/leads', p).then(r => r.data),
  getLead: (id) => api.get(`/admin/crm/leads/${id}`).then(r => r.data),
  updateLead: (id, p) => api.patch(`/admin/crm/leads/${id}`, p).then(r => r.data),
  convertLead: (id, p) => api.post(`/admin/crm/leads/${id}/convert`, p).then(r => r.data),
  products: () => api.get('/admin/crm/products').then(r => r.data),
};

// ─── Small primitives (kept inline to avoid file bloat elsewhere) ────────────
const Field = ({ label, children, required }) => (
  <div>
    <label className="text-[11px] text-slate-400 block mb-1">
      {label}{required && <span className="text-emerald-400 ml-0.5">*</span>}
    </label>
    {children}
  </div>
);
const Input = (p) => (
  <input {...p} className={`w-full h-9 rounded-md bg-slate-900 border border-slate-700 text-white text-sm px-3 focus:outline-none focus:ring-2 focus:ring-emerald-500 ${p.className || ''}`} />
);
const Select = (p) => (
  <select {...p} className={`w-full h-9 rounded-md bg-slate-900 border border-slate-700 text-white text-sm px-2 focus:outline-none focus:ring-2 focus:ring-emerald-500 ${p.className || ''}`}>{p.children}</select>
);
const Textarea = (p) => (
  <textarea {...p} className={`w-full rounded-md bg-slate-900 border border-slate-700 text-white text-sm p-2 focus:outline-none focus:ring-2 focus:ring-emerald-500 ${p.className || ''}`} />
);
const Chip = ({ active, onClick, children, testId, cls }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    className={`px-3 py-1.5 rounded-full text-[12px] font-medium border transition-all whitespace-nowrap ${
      active ? (cls || 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40') : 'bg-slate-800/60 text-slate-400 border-slate-700 hover:text-white'
    }`}
  >
    {children}
  </button>
);

const Modal = ({ title, onClose, children, testId, size = 'md' }) => (
  <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
    <div
      className={`bg-[#0f1611] border border-emerald-900/40 rounded-2xl w-full max-h-[90vh] overflow-y-auto ${size === 'lg' ? 'max-w-3xl' : 'max-w-lg'}`}
      onClick={(e) => e.stopPropagation()}
      data-testid={testId}
    >
      <div className="p-4 border-b border-emerald-900/40 flex items-center justify-between sticky top-0 bg-[#0f1611] z-10">
        <h3 className="text-base font-semibold text-white">{title}</h3>
        <button onClick={onClose} className="text-slate-400 hover:text-white" data-testid={`${testId}-close`}>
          <X className="w-5 h-5" />
        </button>
      </div>
      <div className="p-5">{children}</div>
    </div>
  </div>
);

// ─── AddContactModal ─────────────────────────────────────────────────────────
const AddContactModal = ({ onClose, onSaved }) => {
  const [f, setF] = useState({ name: '', phone: '', email: '', designation: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const canSave = f.name.trim().length >= 2 && (f.phone.trim() || f.email.trim());
  const save = async () => {
    setSaving(true);
    try {
      const c = await crmApi.createContact(f);
      toast.success('Contact saved');
      onSaved?.(c);
      onClose();
    } catch (e) { toast.error(getApiError(e, 'Save failed')); }
    finally { setSaving(false); }
  };
  return (
    <Modal title="Add contact" onClose={onClose} testId="crm-add-contact-modal">
      <div className="space-y-3">
        <Field label="Full name" required>
          <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })}
                 placeholder="e.g. Rahul Sharma" data-testid="crm-contact-name" />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Phone" required>
            <Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })}
                   placeholder="+91 98999 99999" data-testid="crm-contact-phone" />
          </Field>
          <Field label="Email">
            <Input value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })}
                   placeholder="name@company.com" data-testid="crm-contact-email" />
          </Field>
        </div>
        <Field label="Designation">
          <Input value={f.designation} onChange={(e) => setF({ ...f, designation: e.target.value })}
                 placeholder="e.g. Founder / CFO" data-testid="crm-contact-designation" />
        </Field>
        <Field label="Notes">
          <Textarea rows={3} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })}
                    placeholder="Anything worth remembering..." data-testid="crm-contact-notes" />
        </Field>
        <p className="text-[11px] text-slate-500">
          Phone/email is checked against existing contacts — duplicates are auto-merged instead of created twice.
        </p>
        <div className="pt-3 flex justify-end gap-2 border-t border-emerald-900/30">
          <button onClick={onClose} className="px-3 py-1.5 rounded text-sm text-slate-300 hover:text-white">Cancel</button>
          <button
            disabled={!canSave || saving}
            onClick={save}
            data-testid="crm-contact-save-btn"
            className="px-3 py-1.5 rounded text-sm font-medium bg-emerald-500 hover:bg-emerald-400 text-white disabled:opacity-50 flex items-center gap-1"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save contact
          </button>
        </div>
      </div>
    </Modal>
  );
};

// ─── NewLeadModal (2-step) ───────────────────────────────────────────────────
const NewLeadModal = ({ onClose, onSaved, prefillContact, products }) => {
  const [step, setStep] = useState(prefillContact ? 2 : 1);
  const [contact, setContact] = useState(prefillContact || null);
  // step 1 — contact search
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showInline, setShowInline] = useState(false);
  const [inline, setInline] = useState({ name: '', phone: '', email: '', designation: '' });
  // step 2 — lead form
  const [lead, setLead] = useState({
    product_id: '', product_name_manual: '', use_manual_product: false,
    source: 'phone_call', referred_by: '', expected_amount: '',
    notes: '', next_follow_up_date: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!q || q.length < 2) { setResults([]); return; }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const { contacts } = await crmApi.listContacts({ search: q, page: 1, limit: 8 });
        setResults(contacts || []);
      } catch { /* silent */ } finally { setSearching(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const pickContact = (c) => { setContact(c); setStep(2); };
  const canSaveInline = inline.name.trim().length >= 2 && (inline.phone.trim() || inline.email.trim());
  const canSaveLead = !!lead.source && (!!contact || (showInline && canSaveInline));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        source: lead.source,
        product_id: lead.use_manual_product ? null : (lead.product_id || null),
        product_name_manual: lead.use_manual_product ? (lead.product_name_manual || '').trim() || null : null,
        referred_by: lead.referred_by || null,
        expected_amount: lead.expected_amount === '' ? null : parseFloat(lead.expected_amount),
        notes: lead.notes || null,
        next_follow_up_date: lead.next_follow_up_date || null,
      };
      if (contact) payload.contact_id = contact.id;
      else payload.contact = { ...inline, contact_type: 'manual' };
      const created = await crmApi.createLead(payload);
      toast.success('Lead created');
      onSaved?.(created);
      onClose();
    } catch (e) { toast.error(getApiError(e, 'Failed to create lead')); }
    finally { setSaving(false); }
  };

  return (
    <Modal title={step === 1 ? 'New lead — pick contact' : 'New lead — details'}
           onClose={onClose} testId="crm-new-lead-modal" size="lg">
      {step === 1 && (
        <div className="space-y-3">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search contacts by name, phone, email..."
              className="w-full h-10 pl-9 pr-3 rounded-md bg-slate-900 border border-slate-700 text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              data-testid="crm-lead-contact-search"
              autoFocus
            />
          </div>
          <div className="max-h-72 overflow-y-auto border border-emerald-900/30 rounded-lg divide-y divide-emerald-900/20">
            {searching && <div className="p-4 text-center text-slate-400 text-sm"><Loader2 className="w-4 h-4 inline animate-spin" /> searching…</div>}
            {!searching && q.length >= 2 && results.length === 0 && (
              <div className="p-4 text-center text-slate-400 text-sm">
                No contact found for &quot;{q}&quot;. <button onClick={() => { setShowInline(true); setInline({ ...inline, name: q }); setStep(2); }}
                  className="text-emerald-400 underline" data-testid="crm-lead-create-inline-btn">Create new contact</button>
              </div>
            )}
            {results.map((c) => (
              <button key={c.id} onClick={() => pickContact(c)}
                className="w-full text-left p-3 hover:bg-emerald-500/5 flex items-center gap-3"
                data-testid={`crm-lead-pick-contact-${c.id}`}>
                <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center text-emerald-300 text-xs font-bold flex-none">
                  {(c.name || '?').charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-white truncate">{c.name}</div>
                  <div className="text-xs text-slate-500 truncate">
                    {c.phone || '—'} · {c.email || '—'} · {CONTACT_TYPE_META[c.contact_type]?.label || c.contact_type}
                    {c.company_name && ` · ${c.company_name}`}
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 text-slate-500 flex-none" />
              </button>
            ))}
            {!q && (
              <div className="p-6 text-center text-slate-500 text-sm">
                Start typing a name, phone, or email
                <div className="mt-3">
                  <button onClick={() => { setShowInline(true); setStep(2); }}
                          className="text-emerald-400 underline text-sm"
                          data-testid="crm-lead-skip-to-inline">
                    or add a new contact
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-4">
          {contact ? (
            <div className="p-3 rounded-lg bg-emerald-500/5 border border-emerald-500/25 flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center text-emerald-300 text-xs font-bold">
                {(contact.name || '?').charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-white">{contact.name}</div>
                <div className="text-xs text-slate-400">{contact.phone || '—'} · {contact.email || '—'}
                  {contact.company_name && <span className="ml-1 text-emerald-400">· {contact.company_name}</span>}
                </div>
              </div>
              <button onClick={() => { setContact(null); setStep(1); }}
                      className="text-xs text-emerald-400 underline"
                      data-testid="crm-lead-change-contact">Change</button>
            </div>
          ) : (
            <div className="p-3 rounded-lg bg-amber-500/5 border border-amber-500/25 space-y-2">
              <div className="text-[11px] text-amber-300 font-medium">New contact will be created</div>
              <div className="grid grid-cols-2 gap-2">
                <Input placeholder="Name *" value={inline.name} onChange={(e) => setInline({ ...inline, name: e.target.value })} data-testid="crm-lead-inline-name" />
                <Input placeholder="Designation" value={inline.designation} onChange={(e) => setInline({ ...inline, designation: e.target.value })} data-testid="crm-lead-inline-designation" />
                <Input placeholder="Phone *" value={inline.phone} onChange={(e) => setInline({ ...inline, phone: e.target.value })} data-testid="crm-lead-inline-phone" />
                <Input placeholder="Email" value={inline.email} onChange={(e) => setInline({ ...inline, email: e.target.value })} data-testid="crm-lead-inline-email" />
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <Field label="Service interested">
              {lead.use_manual_product ? (
                <div className="space-y-1">
                  <Input value={lead.product_name_manual}
                         onChange={(e) => setLead({ ...lead, product_name_manual: e.target.value })}
                         placeholder="Type the service name" data-testid="crm-lead-product-manual" autoFocus />
                  <button type="button"
                          onClick={() => setLead({ ...lead, use_manual_product: false, product_name_manual: '' })}
                          className="text-[11px] text-emerald-400 underline"
                          data-testid="crm-lead-product-back-to-list">
                    ← Pick from active products
                  </button>
                </div>
              ) : (
                <Select value={lead.product_id}
                        onChange={(e) => {
                          const v = e.target.value;
                          if (v === '__manual__') setLead({ ...lead, use_manual_product: true, product_id: '' });
                          else setLead({ ...lead, product_id: v });
                        }}
                        data-testid="crm-lead-product">
                  <option value="">— Select product —</option>
                  {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                  <option value="__manual__">+ Add manually (not in the list)</option>
                </Select>
              )}
            </Field>
            <Field label="Source" required>
              <Select value={lead.source} onChange={(e) => setLead({ ...lead, source: e.target.value })} data-testid="crm-lead-source">
                {MANUAL_SOURCES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </Select>
            </Field>
            {lead.source === 'referral' && (
              <Field label="Referred by">
                <Input value={lead.referred_by} onChange={(e) => setLead({ ...lead, referred_by: e.target.value })}
                       placeholder="Referrer's name" data-testid="crm-lead-referred-by" />
              </Field>
            )}
            <Field label="Expected amount (₹)">
              <Input type="number" min="0" step="100"
                     value={lead.expected_amount} onChange={(e) => setLead({ ...lead, expected_amount: e.target.value })}
                     placeholder="e.g. 25000" data-testid="crm-lead-expected-amount" />
            </Field>
            <Field label="Next follow-up date">
              <Input type="date" value={lead.next_follow_up_date}
                     onChange={(e) => setLead({ ...lead, next_follow_up_date: e.target.value })}
                     min={todayISO()} data-testid="crm-lead-follow-up" />
            </Field>
          </div>
          <Field label="Notes">
            <Textarea rows={3} value={lead.notes} onChange={(e) => setLead({ ...lead, notes: e.target.value })}
                      placeholder="What are they looking for?" data-testid="crm-lead-notes" />
          </Field>

          <div className="pt-3 flex justify-between border-t border-emerald-900/30">
            <button onClick={() => setStep(1)} className="text-sm text-slate-400 hover:text-white flex items-center gap-1">
              <ChevronLeft className="w-4 h-4" /> Back
            </button>
            <button disabled={!canSaveLead || saving} onClick={save}
                    data-testid="crm-lead-save-btn"
                    className="px-3 py-1.5 rounded text-sm font-medium bg-emerald-500 hover:bg-emerald-400 text-white disabled:opacity-50 flex items-center gap-1">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Create lead
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
};

// ─── LeadDetailDrawer ────────────────────────────────────────────────────────
const LeadDetailDrawer = ({ leadId, products, onClose, onChanged }) => {
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingComment, setSavingComment] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showConvert, setShowConvert] = useState(false);
  // Draft "one-save" state
  const [comment, setComment] = useState('');
  const [status, setStatus] = useState('');
  const [followUp, setFollowUp] = useState('');
  const [expected, setExpected] = useState('');
  const [productId, setProductId] = useState('');
  const [productManual, setProductManual] = useState('');
  const [useManualProduct, setUseManualProduct] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await crmApi.getLead(leadId);
      setLead(d);
      setStatus(d.status || 'new');
      setFollowUp(d.next_follow_up_date || '');
      setExpected(d.expected_amount ?? '');
      setProductId(d.product_id || '');
      // If there's a product_name but no product_id, it's a manual entry.
      setUseManualProduct(!d.product_id && !!d.product_name);
      setProductManual(!d.product_id && d.product_name ? d.product_name : '');
    } catch (e) { toast.error(getApiError(e, 'Failed to load')); onClose(); }
    finally { setLoading(false); }
  }, [leadId, onClose]);

  useEffect(() => { load(); }, [load]);

  const saveOnce = async () => {
    setSaving(true);
    try {
      const payload = {
        status: status || undefined,
        next_follow_up_date: followUp === '' ? '' : followUp,
        expected_amount: expected === '' ? undefined : parseFloat(expected),
        comment_text: comment.trim() || undefined,
        product_id: useManualProduct ? '' : (productId || undefined),
        product_name_manual: useManualProduct ? (productManual.trim() || null) : undefined,
      };
      await crmApi.updateLead(leadId, payload);
      toast.success('Saved');
      setComment('');
      onChanged?.();
      await load();
    } catch (e) { toast.error(getApiError(e, 'Save failed')); }
    finally { setSaving(false); }
  };

  const quickComment = async () => {
    if (!comment.trim()) return;
    setSavingComment(true);
    try {
      await crmApi.updateLead(leadId, { comment_text: comment.trim() });
      setComment('');
      onChanged?.();
      await load();
    } catch (e) { toast.error(getApiError(e, 'Comment failed')); }
    finally { setSavingComment(false); }
  };

  if (loading) {
    return (
      <Modal title="Loading lead" onClose={onClose} testId="crm-lead-drawer" size="lg">
        <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-emerald-500" /></div>
      </Modal>
    );
  }
  if (!lead) return null;

  const linkedCompany = lead.contact_company_name;
  const isExistingClient = !!lead.contact_company_id;

  return (
    <>
      <Modal title={`Lead · ${lead.contact_name || 'Unknown'}`} onClose={onClose} testId="crm-lead-drawer" size="lg">
        <div className="space-y-5">
          {/* Contact strip */}
          <div className="p-3 rounded-lg bg-emerald-500/5 border border-emerald-500/25">
            <div className="flex items-center gap-3 flex-wrap">
              <div className="w-9 h-9 rounded-full bg-emerald-500/20 text-emerald-300 flex items-center justify-center font-bold">
                {(lead.contact_name || '?').charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-white">{lead.contact_name}</div>
                <div className="text-xs text-slate-400 flex items-center gap-3 flex-wrap">
                  {lead.contact_phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {lead.contact_phone}</span>}
                  {lead.contact_email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" /> {lead.contact_email}</span>}
                  {linkedCompany && <span className="flex items-center gap-1 text-emerald-400"><Building2 className="w-3 h-3" /> {linkedCompany}</span>}
                </div>
              </div>
              {isExistingClient && (
                <span className="text-[10.5px] px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                  Existing client
                </span>
              )}
            </div>
          </div>

          {/* Meta */}
          <div className="grid grid-cols-2 gap-3">
            <Field label="Status">
              <Select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="crm-lead-detail-status">
                {Object.entries(STATUS_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </Select>
            </Field>
            <Field label="Next follow-up">
              <Input type="date" value={followUp || ''} onChange={(e) => setFollowUp(e.target.value)}
                     data-testid="crm-lead-detail-followup" />
            </Field>
            <Field label="Expected amount (₹)">
              <Input type="number" min="0" step="100" value={expected}
                     onChange={(e) => setExpected(e.target.value)}
                     data-testid="crm-lead-detail-expected" />
            </Field>
            <Field label="Service">
              {useManualProduct ? (
                <div className="space-y-1">
                  <Input value={productManual}
                         onChange={(e) => setProductManual(e.target.value)}
                         placeholder="Type the service name"
                         data-testid="crm-lead-detail-product-manual" />
                  <button type="button"
                          onClick={() => { setUseManualProduct(false); setProductManual(''); }}
                          className="text-[11px] text-emerald-400 underline"
                          data-testid="crm-lead-detail-product-back-to-list">
                    ← Pick from active products
                  </button>
                </div>
              ) : (
                <Select value={productId}
                        onChange={(e) => {
                          const v = e.target.value;
                          if (v === '__manual__') { setUseManualProduct(true); setProductId(''); }
                          else setProductId(v);
                        }}
                        data-testid="crm-lead-detail-product">
                  <option value="">— Select product —</option>
                  {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                  <option value="__manual__">+ Add manually (not in the list)</option>
                </Select>
              )}
            </Field>
            <Field label="Source">
              <div className="text-sm text-slate-300 py-2">{sourceLabel(lead.source)}
                {lead.referred_by && <span className="text-slate-500 ml-1">· {lead.referred_by}</span>}
              </div>
            </Field>
            <Field label="Created">
              <div className="text-sm text-slate-500 py-2">{fmtTime(lead.created_at)}</div>
            </Field>
          </div>

          {/* Comments — one save writes comment + status + follow-up */}
          <div>
            <div className="text-[11px] text-slate-400 mb-2 flex items-center gap-1">
              <MessageSquare className="w-3 h-3" /> Comments (internal — never visible to client)
            </div>
            <div className="space-y-2 max-h-56 overflow-y-auto">
              {(lead.comments || []).length === 0 && (
                <p className="text-xs text-slate-500 italic">No comments yet.</p>
              )}
              {[...(lead.comments || [])].reverse().map((c) => (
                <div key={c.id} className="p-2.5 rounded-md bg-slate-900/60 border border-slate-800">
                  <div className="text-[11px] text-slate-500 mb-1">
                    <span className="text-slate-300 font-medium">{c.actor_name}</span> · {fmtTime(c.timestamp)}
                  </div>
                  <div className="text-sm text-slate-200 whitespace-pre-wrap">{c.text}</div>
                </div>
              ))}
            </div>
            <Textarea rows={2} value={comment} onChange={(e) => setComment(e.target.value)}
                      placeholder="Add a comment (Ctrl+Enter to quick-add without changing status)"
                      className="mt-2" data-testid="crm-lead-detail-comment-input"
                      onKeyDown={(e) => { if (e.ctrlKey && e.key === 'Enter') quickComment(); }} />
          </div>

          <div className="flex flex-wrap items-center gap-2 justify-between border-t border-emerald-900/30 pt-3">
            <button onClick={() => setShowConvert(true)}
                    disabled={status === 'closed_won'}
                    data-testid="crm-lead-convert-btn"
                    className="px-3 py-1.5 rounded text-sm font-medium bg-emerald-500 hover:bg-emerald-400 text-white disabled:opacity-40 flex items-center gap-1">
              <Trophy className="w-4 h-4" /> Convert to Closed-Won
            </button>
            <div className="flex items-center gap-2">
              <button onClick={quickComment} disabled={!comment.trim() || savingComment}
                      className="px-3 py-1.5 rounded text-sm text-slate-300 hover:text-white border border-slate-700 disabled:opacity-40">
                {savingComment ? <Loader2 className="w-4 h-4 inline animate-spin" /> : 'Add comment'}
              </button>
              <button onClick={saveOnce} disabled={saving}
                      data-testid="crm-lead-detail-save"
                      className="px-3 py-1.5 rounded text-sm font-medium bg-blue-600 hover:bg-blue-500 text-white disabled:opacity-40 flex items-center gap-1">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Save comment + follow-up + status
              </button>
            </div>
          </div>
        </div>
      </Modal>

      {showConvert && (
        <ConvertLeadModal lead={lead} onClose={() => setShowConvert(false)}
                          onDone={() => { setShowConvert(false); onChanged?.(); load(); }} />
      )}
    </>
  );
};

// ─── ConvertLeadModal ────────────────────────────────────────────────────────
const ConvertLeadModal = ({ lead, onClose, onDone }) => {
  const isExisting = !!lead.contact_company_id;
  const [f, setF] = useState({
    final_amount: lead.expected_amount ?? '',
    company_name: '', entity_type: 'private_limited', state: '',
  });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    try {
      const payload = {
        final_amount: f.final_amount === '' ? null : parseFloat(f.final_amount),
        create_company: !isExisting,
        company_name: isExisting ? undefined : f.company_name.trim(),
        entity_type: isExisting ? undefined : f.entity_type,
        state: isExisting ? undefined : f.state,
      };
      const res = await crmApi.convertLead(lead.id, payload);
      if (isExisting) {
        toast.success('Marked Closed-Won. Opening the company…');
      } else {
        toast.success('Company created & lead won.');
      }
      onDone?.(res);
    } catch (e) { toast.error(getApiError(e, 'Convert failed')); }
    finally { setSaving(false); }
  };

  return (
    <Modal title={isExisting ? 'Convert — existing client' : 'Convert — create new company'}
           onClose={onClose} testId="crm-convert-modal">
      <div className="space-y-3">
        {isExisting ? (
          <div className="p-3 rounded-lg bg-emerald-500/5 border border-emerald-500/25 text-sm text-emerald-200">
            Contact is already linked to <span className="font-semibold">{lead.contact_company_name}</span>.
            No new company will be created — we&apos;ll just mark this lead as won.
          </div>
        ) : (
          <>
            <div className="p-3 rounded-lg bg-amber-500/5 border border-amber-500/25 text-xs text-amber-200">
              This will create a new company record pre-filled from the contact. Admin can complete the details later from the Companies tab.
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Company name" required>
                <Input value={f.company_name} onChange={(e) => setF({ ...f, company_name: e.target.value })}
                       placeholder="e.g. Alpha Foods Pvt Ltd" data-testid="crm-convert-company-name" />
              </Field>
              <Field label="Entity type">
                <Select value={f.entity_type} onChange={(e) => setF({ ...f, entity_type: e.target.value })} data-testid="crm-convert-entity">
                  <option value="private_limited">Private Limited</option>
                  <option value="llp">LLP</option>
                  <option value="partnership">Partnership</option>
                  <option value="proprietorship">Proprietorship</option>
                  <option value="one_person_company">OPC</option>
                </Select>
              </Field>
              <Field label="State">
                <Input value={f.state} onChange={(e) => setF({ ...f, state: e.target.value })}
                       placeholder="e.g. Maharashtra" data-testid="crm-convert-state" />
              </Field>
            </div>
          </>
        )}
        <Field label="Final amount (₹)">
          <Input type="number" min="0" step="100" value={f.final_amount}
                 onChange={(e) => setF({ ...f, final_amount: e.target.value })}
                 data-testid="crm-convert-final-amount" />
        </Field>
        <div className="pt-3 flex justify-end gap-2 border-t border-emerald-900/30">
          <button onClick={onClose} className="text-sm text-slate-400 hover:text-white">Cancel</button>
          <button disabled={saving || (!isExisting && !f.company_name.trim())} onClick={submit}
                  data-testid="crm-convert-confirm"
                  className="px-3 py-1.5 rounded text-sm font-medium bg-emerald-500 hover:bg-emerald-400 text-white disabled:opacity-40 flex items-center gap-1">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
            {isExisting ? 'Mark Closed-Won' : 'Create company & win'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

// ─── ContactsPanel ───────────────────────────────────────────────────────────
const ContactsPanel = ({ products, onNewLeadFromContact }) => {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [backfilling, setBackfilling] = useState(false);
  const limit = 10;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await crmApi.listContacts({
        page, limit,
        search: search || undefined,
        contact_type: typeFilter || undefined,
      });
      setRows(r.contacts || []);
      setTotal(r.total || 0);
    } catch (e) { toast.error(getApiError(e, 'Failed to load contacts')); }
    finally { setLoading(false); }
  }, [page, search, typeFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [search, typeFilter]);

  const runBackfill = async () => {
    if (!window.confirm('Pull all directors + client users into contacts? Existing matches will be merged.')) return;
    setBackfilling(true);
    try {
      const r = await crmApi.backfillContacts();
      toast.success(`Backfill done — ${r.created} created, ${r.updated} updated`);
      load();
    } catch (e) { toast.error(getApiError(e, 'Backfill failed')); }
    finally { setBackfilling(false); }
  };

  const pages = Math.max(1, Math.ceil(total / limit));

  return (
    <div className="space-y-4" data-testid="crm-contacts-panel">
      {/* Header row */}
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, phone, email"
            className="w-full h-9 pl-9 pr-3 rounded-md bg-slate-900 border border-slate-700 text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
            data-testid="crm-contacts-search"
          />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Chip active={!typeFilter} onClick={() => setTypeFilter('')} testId="crm-contacts-filter-all">All</Chip>
          <Chip active={typeFilter === 'manual'} onClick={() => setTypeFilter('manual')} testId="crm-contacts-filter-manual">Manual</Chip>
          <Chip active={typeFilter === 'director'} onClick={() => setTypeFilter('director')} testId="crm-contacts-filter-director">Directors</Chip>
          <Chip active={typeFilter === 'client'} onClick={() => setTypeFilter('client')} testId="crm-contacts-filter-client">Clients</Chip>
          <button onClick={runBackfill} disabled={backfilling}
                  data-testid="crm-contacts-backfill"
                  className="text-xs text-slate-300 hover:text-white border border-slate-700 rounded-md px-2.5 py-1.5 flex items-center gap-1">
            {backfilling ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            Sync from platform
          </button>
          <button onClick={() => setShowAdd(true)}
                  data-testid="crm-contacts-add-btn"
                  className="text-xs font-medium bg-emerald-500 hover:bg-emerald-400 text-white rounded-md px-3 py-1.5 flex items-center gap-1">
            <Plus className="w-3.5 h-3.5" /> Add contact
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="rounded-xl border border-emerald-900/30 overflow-hidden bg-[#0d1210]">
        <table className="w-full text-sm">
          <thead className="bg-slate-900/60 text-[11px] text-slate-400 uppercase tracking-wider">
            <tr>
              <th className="text-left px-4 py-2.5">Name</th>
              <th className="text-left px-4 py-2.5">Phone</th>
              <th className="text-left px-4 py-2.5">Email</th>
              <th className="text-left px-4 py-2.5">Company</th>
              <th className="text-left px-4 py-2.5">Type</th>
              <th className="text-left px-4 py-2.5">Open leads</th>
              <th className="text-right px-4 py-2.5">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-emerald-900/20">
            {loading && (
              <tr><td colSpan={7} className="py-8 text-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin inline" /></td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={7} className="py-10 text-center text-slate-500">
                No contacts yet. {typeFilter ? 'Try clearing filters.' : 'Click "Sync from platform" to pull existing directors & clients, or add one manually.'}
              </td></tr>
            )}
            {!loading && rows.map((c) => (
              <tr key={c.id} className="hover:bg-emerald-500/5" data-testid={`crm-contact-row-${c.id}`}>
                <td className="px-4 py-2.5">
                  <div className="text-white font-medium">{c.name || '—'}</div>
                  {c.designation && <div className="text-[11px] text-slate-500">{c.designation}</div>}
                </td>
                <td className="px-4 py-2.5 text-slate-300 font-mono text-[12.5px]">{c.phone || '—'}</td>
                <td className="px-4 py-2.5 text-slate-300 truncate max-w-[220px]">{c.email || '—'}</td>
                <td className="px-4 py-2.5 text-slate-300">{c.company_name || <span className="text-slate-600">—</span>}</td>
                <td className="px-4 py-2.5">
                  <span className={`text-[10.5px] px-2 py-0.5 rounded-full ${CONTACT_TYPE_META[c.contact_type]?.cls || 'bg-slate-800 text-slate-400'}`}>
                    {CONTACT_TYPE_META[c.contact_type]?.label || c.contact_type}
                  </span>
                </td>
                <td className="px-4 py-2.5">
                  <span className={c.open_leads_count > 0 ? 'text-emerald-300 font-semibold' : 'text-slate-500'}>
                    {c.open_leads_count || 0}
                  </span>
                </td>
                <td className="px-4 py-2.5 text-right">
                  <button onClick={() => onNewLeadFromContact(c)}
                          data-testid={`crm-contact-new-lead-${c.id}`}
                          className="text-xs px-2.5 py-1 rounded bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-300 border border-emerald-500/30 flex items-center gap-1 ml-auto">
                    <Plus className="w-3 h-3" /> New Lead
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between text-xs text-slate-400">
        <span>Showing {rows.length ? (page - 1) * limit + 1 : 0}–{(page - 1) * limit + rows.length} of {total}</span>
        <div className="flex items-center gap-2">
          <button disabled={page <= 1} onClick={() => setPage(page - 1)}
                  className="px-2 py-1 rounded border border-slate-700 disabled:opacity-40" data-testid="crm-contacts-prev">‹ Prev</button>
          <span>Page {page} / {pages}</span>
          <button disabled={page >= pages} onClick={() => setPage(page + 1)}
                  className="px-2 py-1 rounded border border-slate-700 disabled:opacity-40" data-testid="crm-contacts-next">Next ›</button>
        </div>
      </div>

      {showAdd && <AddContactModal onClose={() => setShowAdd(false)} onSaved={() => load()} />}
    </div>
  );
};

// ─── LeadsPanel ──────────────────────────────────────────────────────────────
const LeadsPanel = ({ products, openLeadId, onOpenLead, onCloseLead, prefillContact, clearPrefillContact }) => {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState('');
  const [source, setSource] = useState('');
  const [search, setSearch] = useState('');
  const [dueToday, setDueToday] = useState(false);
  const [clientFilter, setClientFilter] = useState(''); // '', 'existing', 'new'
  const [sortBy, setSortBy] = useState('next_follow_up_date');
  const [sortDir, setSortDir] = useState('asc');
  const [summary, setSummary] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const limit = 10;

  const filters = useMemo(() => ({
    status: status || undefined,
    source: source || undefined,
    search: search || undefined,
    due_today: dueToday || undefined,
    only_existing_clients: clientFilter === 'existing' || undefined,
    only_new_prospects: clientFilter === 'new' || undefined,
  }), [status, source, search, dueToday, clientFilter]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [rowsRes, sumRes] = await Promise.all([
        crmApi.listLeads({ page, limit, sort_by: sortBy, sort_dir: sortDir, ...filters }),
        crmApi.leadsSummary(filters),
      ]);
      setRows(rowsRes.leads || []);
      setTotal(rowsRes.total || 0);
      setSummary(sumRes);
    } catch (e) { toast.error(getApiError(e, 'Failed to load leads')); }
    finally { setLoading(false); }
  }, [page, sortBy, sortDir, filters]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [status, source, search, dueToday, clientFilter]);

  const toggleSort = (col) => {
    if (sortBy === col) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(col);
      // Sensible defaults per column type.
      setSortDir(col === 'expected_amount' || col === 'created_at' || col === 'last_comment_at' ? 'desc' : 'asc');
    }
  };

  const pages = Math.max(1, Math.ceil(total / limit));

  // Auto-open new-lead modal when a contact is passed in from the Contacts tab
  useEffect(() => {
    if (prefillContact) setShowNew(true);
  }, [prefillContact]);

  return (
    <div className="space-y-4" data-testid="crm-leads-panel">
      {/* Numbers strip */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard label="Open pipeline" primary={INR(summary.open_pipeline.amount)}
                    secondary={`${summary.open_pipeline.count} lead${summary.open_pipeline.count === 1 ? '' : 's'}`}
                    icon={<Clock className="w-4 h-4 text-emerald-400" />} cls="border-emerald-500/25" testId="crm-summary-open" />
          <StatCard label="Won this month" primary={INR(summary.month.won.amount)}
                    secondary={`${summary.month.won.count} deal${summary.month.won.count === 1 ? '' : 's'}`}
                    icon={<Trophy className="w-4 h-4 text-emerald-400" />} cls="border-emerald-500/25" testId="crm-summary-won" />
          <StatCard label="Lost this month" primary={INR(summary.month.lost.amount)}
                    secondary={`${summary.month.lost.count} deal${summary.month.lost.count === 1 ? '' : 's'}`}
                    icon={<XCircle className="w-4 h-4 text-slate-400" />} cls="border-slate-700" testId="crm-summary-lost" />
          <StatCard label="Win rate" primary={`${summary.month.win_rate_pct}%`}
                    secondary="this month" icon={<CheckCircle2 className="w-4 h-4 text-blue-400" />} cls="border-blue-500/25" testId="crm-summary-rate" />
        </div>
      )}

      {/* Status chips */}
      <div className="flex items-center gap-2 flex-wrap">
        <Chip active={!status} onClick={() => setStatus('')} testId="crm-lead-status-all">All</Chip>
        {Object.entries(STATUS_META).map(([k, v]) => {
          const s = summary?.per_status?.[k];
          const total = k === 'closed_won' || k === 'closed_lost' ? s?.final_amount : s?.expected_amount;
          return (
            <Chip key={k} active={status === k} onClick={() => setStatus(k)}
                  testId={`crm-lead-status-${k}`}
                  cls={v.cls}>
              {v.label} ({s?.count || 0}) · {INR(total || 0)}
            </Chip>
          );
        })}
        <div className="w-px h-6 bg-emerald-900/40 mx-1" />
        <Chip active={dueToday} onClick={() => setDueToday(!dueToday)} testId="crm-lead-due-today">
          Due today ({summary?.due_today_count || 0})
        </Chip>
        <Chip active={clientFilter === 'existing'} onClick={() => setClientFilter(clientFilter === 'existing' ? '' : 'existing')} testId="crm-lead-existing-clients">
          Existing clients ({summary?.existing_clients_count || 0})
        </Chip>
        <Chip active={clientFilter === 'new'} onClick={() => setClientFilter(clientFilter === 'new' ? '' : 'new')} testId="crm-lead-new-prospects">
          New prospects ({summary?.new_prospects_count || 0})
        </Chip>
      </div>

      {/* Source chips */}
      {summary?.per_source && Object.keys(summary.per_source).length > 0 && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[11px] text-slate-500 uppercase tracking-wider mr-1">Source</span>
          <Chip active={!source} onClick={() => setSource('')} testId="crm-lead-source-all">All</Chip>
          {Object.entries(summary.per_source).map(([k, n]) => (
            <Chip key={k} active={source === k} onClick={() => setSource(k)} testId={`crm-lead-source-${k}`}>
              {sourceLabel(k)} ({n})
            </Chip>
          ))}
        </div>
      )}

      {/* Search + New */}
      <div className="flex items-center gap-3 flex-wrap justify-between">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input value={search} onChange={(e) => setSearch(e.target.value)}
                 placeholder="Search leads by contact name, phone, email"
                 className="w-full h-9 pl-9 pr-3 rounded-md bg-slate-900 border border-slate-700 text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                 data-testid="crm-leads-search" />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[11px] text-slate-500 hidden md:inline">Tip: click any column header to sort</span>
          <button onClick={() => setShowNew(true)}
                  data-testid="crm-leads-new-btn"
                  className="text-xs font-medium bg-emerald-500 hover:bg-emerald-400 text-white rounded-md px-3 py-1.5 flex items-center gap-1">
            <Plus className="w-3.5 h-3.5" /> New Lead
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="rounded-xl border border-emerald-900/30 overflow-hidden bg-[#0d1210]">
        <table className="w-full text-sm">
          <thead className="bg-slate-900/60 text-[11px] text-slate-400 uppercase tracking-wider">
            <tr>
              <SortH col="contact_name" label="Contact" sortBy={sortBy} sortDir={sortDir} onToggle={toggleSort} />
              <SortH col="contact_phone" label="Phone" sortBy={sortBy} sortDir={sortDir} onToggle={toggleSort} />
              <SortH col="contact_company_name" label="Company" sortBy={sortBy} sortDir={sortDir} onToggle={toggleSort} />
              <SortH col="product_name" label="Service" sortBy={sortBy} sortDir={sortDir} onToggle={toggleSort} />
              <SortH col="source" label="Source" sortBy={sortBy} sortDir={sortDir} onToggle={toggleSort} />
              <SortH col="expected_amount" label="Amount" align="right" sortBy={sortBy} sortDir={sortDir} onToggle={toggleSort} />
              <SortH col="next_follow_up_date" label="Next follow-up" sortBy={sortBy} sortDir={sortDir} onToggle={toggleSort} />
              <SortH col="last_comment_at" label="Last comment" sortBy={sortBy} sortDir={sortDir} onToggle={toggleSort} />
              <SortH col="status" label="Status" sortBy={sortBy} sortDir={sortDir} onToggle={toggleSort} />
              <th className="text-right px-3 py-2.5 text-[11px] text-slate-400 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-emerald-900/20">
            {loading && <tr><td colSpan={10} className="py-8 text-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin inline" /></td></tr>}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={10} className="py-10 text-center text-slate-500">No leads match the current filters.</td></tr>
            )}
            {!loading && rows.map((l) => (
              <tr key={l.id} className={`hover:bg-emerald-500/5 ${l.follow_up_overdue ? 'bg-amber-500/5' : ''}`}
                  data-testid={`crm-lead-row-${l.id}`}>
                <td className="px-3 py-2.5">
                  <div className="text-white font-medium">{l.contact_name || '—'}</div>
                  {l.contact_company_id && (
                    <span className="text-[10px] text-emerald-400">Existing client</span>
                  )}
                </td>
                <td className="px-3 py-2.5 text-slate-300 font-mono text-[12.5px]">{l.contact_phone || '—'}</td>
                <td className="px-3 py-2.5 text-slate-300">{l.contact_company_name || <span className="text-slate-600">—</span>}</td>
                <td className="px-3 py-2.5 text-slate-300">{l.product_name || <span className="text-slate-600">—</span>}</td>
                <td className="px-3 py-2.5 text-slate-300">{sourceLabel(l.source)}</td>
                <td className="px-3 py-2.5 text-right text-slate-300">
                  {l.status === 'closed_won' ? INR(l.final_amount) : INR(l.expected_amount)}
                </td>
                <td className="px-3 py-2.5">
                  <span className={l.follow_up_overdue ? 'text-amber-300 font-semibold' : l.follow_up_today ? 'text-amber-200' : 'text-slate-400'}>
                    {fmtDate(l.next_follow_up_date)}
                  </span>
                </td>
                <td className="px-3 py-2.5 text-slate-400 text-[12px] italic max-w-[220px] truncate">
                  {l.last_comment_preview || '—'}
                </td>
                <td className="px-3 py-2.5">
                  <span className={`text-[10.5px] px-2 py-0.5 rounded-full border ${STATUS_META[l.status]?.cls || 'bg-slate-800 text-slate-400'}`}>
                    {STATUS_META[l.status]?.label || l.status}
                  </span>
                </td>
                <td className="px-3 py-2.5 text-right">
                  <button onClick={() => onOpenLead(l.id)}
                          data-testid={`crm-lead-open-${l.id}`}
                          className="text-xs px-2.5 py-1 rounded bg-slate-800 hover:bg-slate-700 text-slate-200">
                    Open
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between text-xs text-slate-400">
        <span>Showing {rows.length ? (page - 1) * limit + 1 : 0}–{(page - 1) * limit + rows.length} of {total}</span>
        <div className="flex items-center gap-2">
          <button disabled={page <= 1} onClick={() => setPage(page - 1)}
                  className="px-2 py-1 rounded border border-slate-700 disabled:opacity-40" data-testid="crm-leads-prev">‹ Prev</button>
          <span>Page {page} / {pages}</span>
          <button disabled={page >= pages} onClick={() => setPage(page + 1)}
                  className="px-2 py-1 rounded border border-slate-700 disabled:opacity-40" data-testid="crm-leads-next">Next ›</button>
        </div>
      </div>

      {showNew && (
        <NewLeadModal products={products} prefillContact={prefillContact}
                      onClose={() => { setShowNew(false); clearPrefillContact?.(); }}
                      onSaved={() => load()} />
      )}

      {openLeadId && (
        <LeadDetailDrawer leadId={openLeadId} products={products}
                          onClose={onCloseLead} onChanged={load} />
      )}
    </div>
  );
};

const StatCard = ({ label, primary, secondary, icon, cls, testId }) => (
  <div className={`p-3 rounded-lg bg-slate-900/40 border ${cls || 'border-slate-700'}`} data-testid={testId}>
    <div className="text-[11px] text-slate-400 uppercase tracking-wider flex items-center gap-1.5">{icon} {label}</div>
    <div className="text-lg font-bold text-white mt-1">{primary}</div>
    <div className="text-[11px] text-slate-500">{secondary}</div>
  </div>
);

// Sortable table-header. Stateless — parent owns sortBy/sortDir + toggle handler.
const SortH = ({ col, label, align = 'left', sortBy, sortDir, onToggle }) => {
  const active = sortBy === col;
  const Arrow = active ? (sortDir === 'asc' ? ArrowUp : ArrowDown) : null;
  return (
    <th className={`px-3 py-2.5 ${align === 'right' ? 'text-right' : 'text-left'}`}>
      <button
        onClick={() => onToggle(col)}
        data-testid={`crm-lead-sort-${col}`}
        className={`inline-flex items-center gap-1 text-[11px] uppercase tracking-wider font-semibold ${
          active ? 'text-emerald-300' : 'text-slate-400 hover:text-white'
        }`}
      >
        {label}
        {Arrow && <Arrow className="w-3 h-3" />}
      </button>
    </th>
  );
};

// ─── CRMTab (root) ───────────────────────────────────────────────────────────
export const CRMTab = () => {
  const [sub, setSub] = useState('contacts'); // 'contacts' | 'leads'
  const [products, setProducts] = useState([]);
  const [openLeadId, setOpenLeadId] = useState(null);
  const [prefillContact, setPrefillContact] = useState(null);

  useEffect(() => {
    crmApi.products().then(setProducts).catch(() => setProducts([]));
  }, []);

  const handleNewLeadFromContact = (c) => {
    setPrefillContact(c);
    setSub('leads');
  };

  return (
    <div className="space-y-5" data-testid="crm-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-semibold text-white flex items-center gap-2">
            <ContactIcon className="w-5 h-5 text-emerald-400" /> CRM
          </h1>
          <p className="text-slate-500 text-[12.5px] mt-0.5">
            Contacts are the base — every lead starts from a contact. Duplicates are auto-merged by phone or email.
          </p>
        </div>
        <div className="flex bg-slate-900/60 border border-emerald-900/30 rounded-lg p-1">
          <button onClick={() => setSub('contacts')}
                  data-testid="crm-subtab-contacts"
                  className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    sub === 'contacts' ? 'bg-emerald-500 text-white' : 'text-slate-400 hover:text-white'
                  }`}>Contacts</button>
          <button onClick={() => setSub('leads')}
                  data-testid="crm-subtab-leads"
                  className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    sub === 'leads' ? 'bg-emerald-500 text-white' : 'text-slate-400 hover:text-white'
                  }`}>Leads</button>
        </div>
      </div>

      {sub === 'contacts' && (
        <ContactsPanel products={products} onNewLeadFromContact={handleNewLeadFromContact} />
      )}
      {sub === 'leads' && (
        <LeadsPanel products={products}
                    openLeadId={openLeadId}
                    onOpenLead={setOpenLeadId}
                    onCloseLead={() => setOpenLeadId(null)}
                    prefillContact={prefillContact}
                    clearPrefillContact={() => setPrefillContact(null)} />
      )}
    </div>
  );
};

export default CRMTab;
